import { useState } from 'react';

import {
  Badge,
  Button,
  InputErrorMessage,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@commenda/kakashi';

import { saveServiceAuditFindingFeedback } from '@commenda/api/agent-platform/service-auditor/saveServiceAuditFindingFeedback';
import { IServiceAuditorFinding } from '@commenda/api/agent-platform/service-auditor/types';

import { useNotification } from '../../../hooks/useNotification';
import {
  IServiceAuditFeedbackErrors,
  IServiceAuditFeedbackValues,
  ServiceAuditFeedbackHelper,
} from '../helpers/ServiceAuditFeedbackHelper';

interface IServiceAuditFindingFeedbackFormProps {
  finding: IServiceAuditorFinding;
  // Refreshes the report so the saved state and the reviewed counts move. Null keeps the feedback read only.
  onFeedbackSaved: (() => Promise<unknown>) | null;
}

/** The reviewer's verdict and reason on one finding, with its saved and editing states. */
export const ServiceAuditFindingFeedbackForm = ({
  finding,
  onFeedbackSaved,
}: IServiceAuditFindingFeedbackFormProps) => {
  const { setNotification } = useNotification();
  const { feedback } = finding;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [values, setValues] = useState<IServiceAuditFeedbackValues>(() =>
    ServiceAuditFeedbackHelper.getInitialValues(null)
  );
  const [errors, setErrors] = useState<IServiceAuditFeedbackErrors>({});
  const fieldId = `service-audit-feedback-${finding.id}`;

  const updateValues = (
    changes: Partial<IServiceAuditFeedbackValues>,
    field: keyof IServiceAuditFeedbackErrors
  ) => {
    setValues((current) => ({ ...current, ...changes }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const startEditing = () => {
    setValues(ServiceAuditFeedbackHelper.getInitialValues(feedback));
    setErrors({});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setErrors({});
    setIsEditing(false);
  };

  const saveFeedback = async () => {
    if (!onFeedbackSaved) return;
    const validation = ServiceAuditFeedbackHelper.validateFeedback(values);
    setErrors(validation.errors);
    if (!validation.input) return;
    setIsSaving(true);
    const result = await saveServiceAuditFindingFeedback({
      findingId: finding.id,
      ...validation.input,
    });
    if (!result.success) {
      setIsSaving(false);
      setNotification({
        ...ServiceAuditFeedbackHelper.SAVE_FAILED_TOAST,
        valid: false,
        show: true,
      });
      return;
    }
    // Wait for the refreshed report so the saved state replaces the form without a flash of the empty form.
    try {
      await onFeedbackSaved();
    } catch {
      // The save itself succeeded; the next report refresh shows it.
    }
    setIsSaving(false);
    setIsEditing(false);
    setNotification({
      ...ServiceAuditFeedbackHelper.SAVED_TOAST,
      valid: true,
      show: true,
    });
  };

  const savedFeedback = feedback && !isEditing ? feedback : null;
  const savedVerdictBadge = savedFeedback
    ? ServiceAuditFeedbackHelper.getVerdictBadge(savedFeedback.verdict)
    : null;

  return (
    <div className="flex flex-col gap-md rounded-lg border border-border-primary bg-bg-secondary p-lg">
      <span className="txt-small-medium text-text-primary">
        {ServiceAuditFeedbackHelper.BLOCK_LABEL}
      </span>
      {savedFeedback && savedVerdictBadge ? (
        <>
          <div className="flex flex-wrap items-center gap-md">
            <Badge size="sm" variant={savedVerdictBadge.variant}>
              {savedVerdictBadge.label}
            </Badge>
          </div>
          {savedFeedback.reason && (
            <p className="break-words txt-small-regular text-text-secondary">
              {savedFeedback.reason}
            </p>
          )}
          <span className="txt-extra-small-regular text-text-tertiary">
            {ServiceAuditFeedbackHelper.getSavedMeta(savedFeedback)}
          </span>
          {onFeedbackSaved && (
            <Button
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={startEditing}
            >
              {ServiceAuditFeedbackHelper.EDIT_LABEL}
            </Button>
          )}
        </>
      ) : !onFeedbackSaved ? (
        <p className="txt-extra-small-regular text-text-tertiary">
          {ServiceAuditFeedbackHelper.FEEDBACK_CLOSED_TEXT}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-xs">
            <Label id={`${fieldId}-verdict`}>
              {ServiceAuditFeedbackHelper.VERDICT_QUESTION}
            </Label>
            <RadioGroup
              aria-labelledby={`${fieldId}-verdict`}
              value={values.verdict ?? ''}
              onValueChange={(value) =>
                updateValues(
                  { verdict: ServiceAuditFeedbackHelper.findVerdict(value) },
                  'verdict'
                )
              }
              disabled={isSaving}
            >
              {ServiceAuditFeedbackHelper.VERDICT_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center gap-md">
                  <RadioGroupItem
                    value={option.value}
                    id={`${fieldId}-${option.value}`}
                  />
                  <label
                    htmlFor={`${fieldId}-${option.value}`}
                    className="txt-small-regular text-text-primary"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </RadioGroup>
            {errors.verdict && <InputErrorMessage message={errors.verdict} />}
          </div>
          <div className="flex flex-col gap-xs">
            <Label htmlFor={`${fieldId}-reason`}>
              {ServiceAuditFeedbackHelper.REASON_LABEL}
            </Label>
            <Textarea
              id={`${fieldId}-reason`}
              value={values.reason}
              onChange={(event) =>
                updateValues({ reason: event.target.value }, 'reason')
              }
              placeholder={ServiceAuditFeedbackHelper.REASON_PLACEHOLDER}
              error={errors.reason}
              disabled={isSaving}
              className="[field-sizing:content]"
            />
          </div>
          <div className="flex items-center gap-md">
            <Button size="sm" onClick={saveFeedback} disabled={isSaving}>
              {isSaving
                ? ServiceAuditFeedbackHelper.SAVING_LABEL
                : ServiceAuditFeedbackHelper.SAVE_LABEL}
            </Button>
            {isEditing && feedback && (
              <Button
                size="sm"
                variant="secondary"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                {ServiceAuditFeedbackHelper.CANCEL_EDIT_LABEL}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
